/**
 * PremiumPackagesListener
 *
 * Listens for SecureStorage changes to user premium packages via eventBus and
 * updates Redux state accordingly (mirrors Flutter's Hive stream subscription
 * in PremiumPackagesNotifier), and additionally starts the Firestore live
 * listener that Flutter starts at app boot via
 * `PremiumPackagesService._initUserPremiumPackages`
 * (lib/services/premium_packages_service.dart:13-70) — that listener is the
 * source of the SecureStorage writes this component mirrors into Redux.
 *
 * Renders nothing — mount alongside AppInitializer in the app tree.
 *
 * Why `eventBus.on` directly (vs. `useListenToSecureStorageUpdates`):
 * Two reasons. (1) The event payload is discarded — we use any write under a
 * KNOWN_PRODUCT_IDS key as a "packages changed, refresh" signal and call
 * `GetUserPremiumPackagesUseCase` to re-aggregate the full list. (2) Multiple
 * keys are watched (one per product ID); subscribing in a loop with
 * `eventBus.on` is cleaner than three separate hook calls plus manual
 * change-detection across them. Compare to AuthUserListener, which watches a
 * single key whose value IS the source of truth and benefits from the hook's
 * typed `data`.
 *
 * Startup race: not exposed here, but only because this listener does NOT
 * perform an initial storage read on mount — it just registers `eventBus.on`
 * subscriptions, and writes (which trigger the eventBus) can only happen
 * after storage is initialized. Note that `isAuthenticated` flipping to true
 * does NOT imply storage is initialized: on fresh sign-in, RootScreen's
 * onAuthStateChanged dispatches setFirebaseUserId before LoginPage
 * completes `initializeSecureStorageUseCase` (LoginPage arms
 * `isInitializing` to bridge this gap — see ExclusiveContentListener /
 * UserDeviceListener for the gate pattern). If anyone later adds an initial
 * `reloadPremiumPackages()` call inside the effect below, also gate on
 * `!isInitializing`.
 */
import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import type { AppDispatch, RootState } from '@/presentation/store';
import { setUserPremiumPackages, clearUserPremiumPackages } from '@/presentation/store/slices/premiumPackages/premiumPackagesSlice';
import { GetUserPremiumPackagesUseCase } from '@/domain/usecases/premiumPackage/getUserPremiumPackagesUseCase';
import { subscribeToPremiumPackagesLiveChangesUseCase } from '@/domain/usecases/premiumPackage/subscribeToPremiumPackagesLiveChangesUseCase';
import { SECURE_STORAGE_KEYS } from '@/domain/entities/secureStorage/secureStorageKeys';
import { PremiumPackageType } from '@/domain/enums/premiumPackages/PremiumPackageType';
import { eventBus } from '@/infrastructure/eventBus';

const KNOWN_PRODUCT_IDS = [
  PremiumPackageType.consumerPro,
  PremiumPackageType.creatorPro,
  PremiumPackageType.identityPro,
];

export const PremiumPackagesListener = () => {
  const dispatch = useDispatch<AppDispatch>();
  const isAuthenticated = useSelector((state: RootState) => state.auth.isAuthenticated);
  const firebaseUserId = useSelector((state: RootState) => state.auth.firebaseUserId);
  const isInitializing = useSelector((state: RootState) => state.auth.isInitializing);

  // Clear premium packages when user logs out
  useEffect(() => {
    if (!isAuthenticated) {
      dispatch(clearUserPremiumPackages());
    }
  }, [isAuthenticated, dispatch]);

  useEffect(() => {
    if (!isAuthenticated) return;

    const reloadPremiumPackages = async () => {
      const useCase = new GetUserPremiumPackagesUseCase();
      const packages = await useCase.execute();
      dispatch(setUserPremiumPackages(packages));
    };

    // Subscribe to SecureStorage changes for each known user premium package key.
    // When a change is detected (e.g. after a sync), reload all user premium packages into Redux.
    const unsubscribers = KNOWN_PRODUCT_IDS.map((productId) => {
      const key = SECURE_STORAGE_KEYS.USER_PREMIUM_PACKAGES + productId;
      return eventBus.on(key, () => {
        reloadPremiumPackages();
      });
    });

    return () => {
      unsubscribers.forEach((unsubscribe) => unsubscribe());
    };
  }, [isAuthenticated, dispatch]);

  useEffect(() => {
    if (!isAuthenticated || !firebaseUserId || isInitializing) return;

    const unsubscribe = subscribeToPremiumPackagesLiveChangesUseCase.execute(firebaseUserId);

    return unsubscribe;
  }, [isAuthenticated, firebaseUserId, isInitializing]);

  return null;
};
